import { Breadcrumb, Button, Col, Row, Table, message, Card, Spin, Modal } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as Back } from '../../../Images/Back.svg';
import { fetchInquiryById } from '../../../slices/inquiry';
import { PrinterOutlined, FileExcelOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { CSVLink } from 'react-csv';
import * as XLSX from 'xlsx';
import logo from '../../../Images/logo.svg';

const ViewInquiry = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const printRef = useRef();
  const [inquiry, setInquiry] = useState(null);
  const [loading, setLoading] = useState(false);
  const { user: currentUser } = useSelector(state => state.auth);
  const tableRef = useRef(null);
  const [modalTitle, setModalTitle] = useState('');
  const [popupData, setPopupData] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  function getFileExtension(url) {
    return url.split('.').pop().split(/\#|\?/)[0];
  }

  const showModal = async (data, title) => {

    let extension = getFileExtension(data);
    const isPdf = extension === 'pdf';
    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(
      extension,
    );
    if (isImage) {
      setModalTitle(title);
      setPopupData(data);
      setIsModalVisible(true);
    } else if (isPdf) {
      window.open(data, '_blank', 'noopener,noreferrer');
    } else {
      const response = await fetch(data);
      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }
      const blobData = await response.blob();
      var blob = new Blob([blobData], { type: 'application/pdf' });
      var blobURL = URL.createObjectURL(blob);
      window.open(blobURL);
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setPopupData(null);
    setModalTitle('');
  };

  useEffect(() => {
    const fetchInquiry = async () => {
      try {
        setLoading(true);
        dispatch(fetchInquiryById(id))
          .unwrap()
          .then(response => {
            if (response) {
              setInquiry(response?.data);
            }
          })
          .catch(error => {
            message.error('Failed to fetch inquiry details');
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        setLoading(false);
        message.error('An error occurred while fetching inquiry details');
      }
    };

    if (id) {
      fetchInquiry();
    }
  }, [id, dispatch]);


  const contactColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'SrNo',
      key: 'SrNo',
      render: (text, record, index) => index + 1,
      width: 80,
    },
    {
      title: 'Person Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Designation',
      dataIndex: 'designation',
      key: 'designation',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile',
      key: 'mobile',

    },
  ];



  const technicalDetailsColumns = [
    {
      title: 'Sr. No.',
      dataIndex: 'sr_no',
      key: 'sr_no',
      render: (text, record, index) => index + 1,
    },
    {
      title: 'Section',
      dataIndex: 'section_no',
      key: 'section_no',
    },
    {
      title: 'Desription',
      dataIndex: 'description',
      key: 'description',
      align: 'center',
      render: (text) => text && text.toString().trim() !== '' ? text : 'NA'
    },
    {
      title: 'Drawing File',
      dataIndex: 'drawing_attachment',
      key: 'drawing_attachment',
      align: 'center',
      render: (text, record) => {
        if (text !== null) {
          return <Button
            type="secondry"
            onClick={() =>
              showModal(text, 'Drawing Attachment')
            }
          >
            View File
          </Button>;
        }
      },
    },
    {
      title: 'Standard Confirmation',
      dataIndex: 'standard_confirmation',
      key: 'standard_confirmation',
      align: 'center',
      render: (text, record) => {
        const value = text?.toString().trim();

        if (!value) {
          return 'NA';
        }

        if (value === 'Others') {
          return (
            <span>
              {value} ({record?.standard_confirmation_other || 'NA'})
            </span>
          );
        }

        return value;
      },
    },
    {
      title: 'Length (mm)',
      dataIndex: 'length',
      key: 'length',
      align: 'center',
    },
    {
      title: 'Tolerance (mm)',
      dataIndex: 'tolerance',
      key: 'tolerance',
      align: 'center',
      render: (_, record) => {
        const minus = record.tolerance_minus ?? 0;
        const plus = record.tolerance_plus ?? 0;
        return `-${minus} / +${plus}`;
      },
    },
    {
      title: 'Alloy',
      dataIndex: 'alloy_detail',
      key: 'alloy_detail',
      align: 'center',
      render: (_, record) => {
        const alloyName = record?.alloy_detail?.name || 'N/A';
        const alloyCode = record?.alloy_detail?.color_code || 'N/A';
        return (
          <span>
            {alloyName} ({alloyCode})
          </span>
        );
      }
    },
    {
      title: 'Temper',
      dataIndex: 'temper_detail',
      key: 'temper_detail',
      align: 'center',
      render: (_, record) => {
        const temperName = record?.temper_detail?.name || 'N/A';
        const temperCode = record?.temper_detail?.code || 'N/A';

        return (
          <span>
            {temperName} ({temperCode})
          </span>
        );
      },
    },
    {
      title: 'Post Operation',
      dataIndex: 'post_operation',
      key: 'post_operation',
      align: 'center',
      render: (text, record) => {
        const value = text?.toString().trim();

        if (!value) {
          return 'NA';
        }

        if (value === 'Others') {
          return (
            <span>
              {value} ({record?.post_operation_other || 'NA'})
            </span>
          );
        }

        return value;
      },
    },
    {
      title: 'End Application',
      dataIndex: 'end_application',
      key: 'end_application',
      align: 'center'
    },
    {
      title: 'Surface Finish',
      dataIndex: 'surface_finish_detail',
      key: 'surface_finish_detail',
      align: 'center',
      render: (surface_finish_detail) => {
        if (surface_finish_detail && surface_finish_detail.length > 0) {
          return surface_finish_detail.map(item => item.name).join(', ');
        }
        return 'N/A';
      },
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <div className="sticyaction">
        <Row className="mb-0" justify="space-between" align="middle">
          <Col>
            <h2 style={{ margin: 0 }}>View Inquiry</h2>
            <div className="bredcrumbwrp">
              <Link to="/inquiry-list" className="back">
                <Back /> BACK
              </Link>
              <Breadcrumb
                items={[
                  { title: <Link to="/dashboard">Home</Link> },
                  { title: <Link to="/inquiry-list">Inquiry</Link> },
                  { title: 'View Inquiry' },
                ]}
              />
            </div>
          </Col>
          <div style={{ display: 'flex', gap: '10px' }}>
            {currentUser.permissions.permissions.inquiry.includes(
              'change_inquiry',
            ) && (
                <Button
                  type="primary"
                  style={{ marginBottom: '20px' }}
                  onClick={() => navigate(`/edit-inquiry/${inquiry?.id}`)}
                >
                  Edit Inquiry
                </Button>
              )}
            {inquiry?.status !== 'Quotation' && inquiry?.status !== 'SalesOrder' && (
              <Button
                type="primary"
                icon={<PlusOutlined />}
                style={{
                  marginBottom: '20px',
                  float: 'center',
                  marginRight: '10px',
                }}
                onClick={() =>
                  navigate(`/convert-inquiry-quotation/${inquiry?.id}`)
                }
              >
                Convert To Quotation
              </Button>
            )}
          </div>
        </Row>
      </div>

      <div className="px-4">
        <Card title="Customer Information" style={{ marginBottom: '20px' }}
          styles={{ header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' } }}
        >
          <Row gutter={[24, 16]} className="viewform">


            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Inquiry Number</label>
              <div className="">{inquiry?.inquiry_number || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Inquiry Date</label>
              <div className="">{inquiry?.inquiry_date ? dayjs(inquiry?.inquiry_date).format('DD-MM-YYYY hh:mm A') : 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Customer Name</label>
              <div className="">{inquiry?.customer_name || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Status</label>
              <div className="">{inquiry?.status || 'N/A'}</div>
            </Col>
            {inquiry?.regret_reason !== null && (
              <Col xs={24} sm={12} lg={8} xl={6}>
                <label className="customlabel">Regreat Reason</label>
                <div className="">{inquiry?.regret_reason || 'N/A'}</div>
              </Col>
            )}
            {inquiry?.feasiblity_description !== null && (
              <Col xs={24} sm={12} lg={8} xl={6}>
                <label className="customlabel">Feasible Description</label>
                <div className="">{inquiry?.feasiblity_description || 'N/A'}</div>
              </Col>
            )}
            {inquiry?.feasiblity_attachment !== null && (
              <Col xs={24} sm={12} lg={8} xl={6}>
                <label className="customlabel">Feasible Attachment</label>
                <div className="">


                  <Button
                    type="secondry"
                    onClick={() =>
                      showModal(inquiry?.source_attachment, 'Feasible Attachment')
                    }
                  >
                    View File
                  </Button>

                </div>
              </Col>
            )}
          </Row>
        </Card>


        <Card
          title="Contact Persons"
          style={{ marginBottom: '20px' }}
          styles={{
            header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' }
          }}
        >
          <Table
            dataSource={inquiry?.contact_persons}
            columns={contactColumns}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        </Card>

        <Card
          title="Technical Details"
          style={{ marginBottom: '20px' }}
          styles={{
            header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' }
          }}
        >
          <Table
            dataSource={inquiry?.inquiry_details}
            columns={technicalDetailsColumns}
            pagination={false}

          />
        </Card>

        <Card title="Quantity Details" style={{ marginBottom: '20px' }}
          styles={{ header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' } }}
        >
          <Row gutter={[24, 16]} className="viewform">


            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Initial Requirement (Kgs)</label>
              <div className="">{inquiry?.initial_requirement || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Annual Requirement (Kgs)</label>
              <div className="">{inquiry?.annual_requirement ? inquiry?.annual_requirement : 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Source of Inquiry</label>
              <div className="">{inquiry?.inquiry_source === 'Open_Form' ? inquiry?.inquiry_source.replaceAll("_", " ") : inquiry?.inquiry_source || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Source of Inquiry Attachment</label>
              <div className="">

                {inquiry?.source_attachment !== null ? (
                  <Button
                    type="secondry"
                    onClick={() =>
                      showModal(inquiry?.source_attachment, 'Source of Inquiry Attachment')
                    }
                  >
                    View File
                  </Button>
                ) : (
                  <div className="customvalue">N/A</div>
                )}
              </div>
            </Col>
          </Row>
        </Card>

        <Card title="Other Relavant Information" style={{ marginBottom: '20px' }}
          styles={{ header: { backgroundColor: '#f0f2f5', fontWeight: 'bold' } }}
        >
          <Row gutter={[24, 16]} className="viewform">


            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Special Notes</label>
              <div className="">{inquiry?.special_notes || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Certifications Required</label>
              <div className="">{inquiry?.certifications_required ? inquiry?.certifications_required : 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Packaging Requirements</label>
              <div className="">{inquiry?.packaging_requirements || 'N/A'}</div>
            </Col>
            <Col xs={24} sm={12} lg={8} xl={6}>
              <label className="customlabel">Additional Notes</label>
              <div className="">{inquiry?.additional_notes || 'N/A'}</div>
            </Col>

          </Row>
        </Card>
        <Modal
          title={modalTitle}
          open={isModalVisible}
          onCancel={handleCancel}
          footer={null}
          width={800}
        >
          {popupData ? (
            <img src={popupData} alt="" style={{ width: '100%', height: 'auto' }} />
          ) : (
            <p>No Image available.</p>
          )}
        </Modal>
      </div>
      <div ref={printRef} style={{ display: 'none' }}></div>

      <style jsx>{`
        .customlabel {
          font-weight: 600;
          color: #555;
          font-size: 12px;
          letter-spacing: 0.5px;
          margin-bottom: 4px;
          display: block;
        }

        . {
          font-size: 14px;
          color: #333;
          font-weight: 500;
          padding: 8px 12px;
          background: #f8f9fa;
          border-radius: 4px;
          border-left: 3px solid #007bff;
          min-height: 36px;
          display: flex;
          align-items: center;
        }

        .viewform .ant-col {
          margin-bottom: 16px;
        }

        .sticyaction {
          position: sticky;
          top: 0;
          z-index: 100;
          background: white;
          padding: 16px 24px;
          border-bottom: 1px solid #e8e8e8;
          margin-bottom: 20px;
        }

        .pagenamerow h2 {
          color: #1f2937;
          font-size: 24px;
          font-weight: 600;
        }

        .bredcrumbwrp {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 8px;
        }

        .back {
          display: flex;
          align-items: center;
          gap: 6px;
          color: #6b7280;
          text-decoration: none;
          font-weight: 500;
          padding: 6px 12px;
          border-radius: 6px;
          transition: all 0.2s;
        }

        .back:hover {
          background: #f3f4f6;
          color: #374151;
        }

        .ant-card {
          border-radius: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
        }

        .ant-card-head {
          border-bottom: 2px solid #e5e7eb;
        }

        .ant-card-head-title {
          font-size: 16px;
          font-weight: 600;
          color: #1f2937;
        }

        .ant-table-thead > tr > th {
          background: #f8fafc;
          font-weight: 600;
          color: #374151;
          border-bottom: 2px solid #e5e7eb;
        }

        .ant-table-tbody > tr > td {
          padding: 12px 16px;
        }

        .ant-table-tbody > tr:hover > td {
          background: #f8fafc;
        }

        .ant-btn {
          border-radius: 6px;
          font-weight: 500;
          transition: all 0.2s;
        }

        .ant-btn-primary {
          background: #2563eb;
          border-color: #2563eb;
        }

        .ant-btn-primary:hover {
          background: #1d4ed8;
          border-color: #1d4ed8;
        }

        @media (max-width: 768px) {
          .sticyaction {
            padding: 12px 16px;
          }

          .px-4 {
            padding-left: 16px !important;
            padding-right: 16px !important;
          }

          . {
            font-size: 13px;
            padding: 6px 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default ViewInquiry;
